import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Link,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  makeStyles,
  withStyles
} from '@material-ui/core';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import UserApi from '../../../utils/apiManager/constrollers/userApi';
import { store } from '../../../redux/store';
import { showError } from '../../../redux/reducers/alerts/actions';
import SignInService from '../../auth/SignInService';
import history from '../../../utils/history';
import { getUser } from '../../../redux/reducers/user/selectors';
import { translate } from '../../../translations/subscriptions.translation';
import { getCurrentLanguage } from '../../../utils/utils';
import { updateUser } from '../../../redux/reducers/user/actions';

const states = [
  {
    value: 'en-US',
    label: 'English'
  },
  {
    value: 'ru-RU',
    label: 'Russian'
  }
];

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '20px 0 0 0',
    float: 'left'
  },
  cardContent: {
    float: 'left',
    width: '100%'
  },
  planCards: {
    width: 240,
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  buttonSubscribe: {
    margin: '20px auto'
  },
  buttonSubscribeNew: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20px auto 20px auto',
  },
  customText: {
    height: 50
  }
}));

const ColorButton = withStyles((theme) => ({
  root: {
    color: '#ffffff',
    width: '120px',
    backgroundColor: '#4caf50',
    '&:hover': {
      backgroundColor: '#4caf50',
    },
  },
}))(Button);

const ProfileDetails = ({
  className,
  user,
  updateUser: storeUserUpdate,
  ...rest
}) => {
  const classes = useStyles();
  const [values, setValues] = useState(user);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const userApi = await UserApi.getInstance();
      const userData = await userApi.fetchUser();
      setValues(userData);
      storeUserUpdate(userData);
      const subs = await userApi.fetchSubscriptions();
      const planRank = (p) => {
        if (p.paymentBackend === 'free') return 0;
        if (p.paymentBackend === 'paypal' && p.plan_id) return 1;
        return 2;
      };
      setSubscriptions((subs || []).sort((a, b) => planRank(a) - planRank(b)));
    };
    fetchData();
  }, []);

  const handleChange = async (event) => {
    const { value, name } = event.target;
    const userApi = await UserApi.getInstance();
    await userApi.updateUserLocale(value);
    storeUserUpdate({
      ...user,
      locale: value,
    });
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleExportData = async () => {
    const userApi = await UserApi.getInstance();
    const data = await userApi.fetchUser();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'userdata.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateCurrentUser = async (subscription, allocated) => {
    storeUserUpdate({
      ...user,
      subscription,
      limits: {
        ...user.limits,
        month: { used: user.limits?.month?.used ?? 0, allocated }
      }
    });
  };

  const formatPrice = (price, currency) => {
    const amount = price ?? 0;
    const symbol = !currency || currency === 'USD' ? '$' : '';
    const suffix = currency && currency !== 'USD' ? ` ${currency}` : '';
    return `${symbol}${amount}${suffix}`;
  };

  const formatRetentionDays = (days) => {
    if (!days) return null;
    if (days >= 365 && days % 365 === 0) {
      const years = days / 365;
      return years === 1 ? '1 year' : `${years} years`;
    }
    return `${days} days`;
  };

  const createSubscription = async (data, actions, planId) => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      return actions.subscription.create({
        plan_id: planId,
        custom_id: `${userId}:${window['stage']}:${values.email}`.substring(0, 127),
      });
    } else {
      const signService = await SignInService.getInstance();
      store.dispatch(showError(true, 'Something went wrong, please login again.'));
      await signService.signOut();
      history.push('/app/login');
      window.location.reload();
    }
  };

  return (
    <form
      autoComplete="off"
      noValidate
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Card>
        <CardHeader
          title={<FormattedMessage id="label.profile" defaultMessage="Profile"/>}
        />
        <Divider />
        <CardContent className={classes.cardContent}>
          <Grid
            container
            spacing={2}
            className={classes.cardContent}
          >
            <Grid
              item
              md={3}
              xl={12}
            >
              <TextField
                fullWidth
                label={<FormattedMessage id="label.first.name" defaultMessage="First name"/>}
                name="firstName"
                onChange={handleChange}
                required
                value={values.firstname}
                variant="outlined"
                disabled
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label={<FormattedMessage id="label.last.name" defaultMessage="Last name"/>}
                name="lastName"
                onChange={handleChange}
                required
                value={values.surname}
                variant="outlined"
                disabled
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label="Email"
                name="email"
                onChange={handleChange}
                required
                value={values.email}
                variant="outlined"
                disabled
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                label={<FormattedMessage id="label.select.language" defaultMessage="Select Language"/>}
                name="locale"
                onChange={handleChange}
                required
                select
                SelectProps={{ native: true }}
                value={values.locale}
                variant="outlined"
              >
                {states.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  {'Your email is shared with PayPal when you create a subscription. See our '}
                  <Link component={RouterLink} to="/privacy-policy">Privacy Policy</Link>
                  {' for details.'}
                </Typography>
              </Box>
              <Box mt={1}>
                <Button variant="outlined" color="primary" onClick={handleExportData}>
                  <FormattedMessage id="label.export.data" defaultMessage="Export My Data" />
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <div padding="20px">
        <Typography>
          &nbsp;
        </Typography>
      </div>
      <Card>
        <CardHeader
          title={<FormattedMessage id="label.subscription" defaultMessage="Subscription plan"/>}
        />
        <Divider />
        <Grid
          container
          spacing={3}
          flex-wrap="wrap"
          justify-content="space-evenly"
        >
          <PayPalScriptProvider options={{'client-id': window['ppClientId'], vault: true, intent: 'subscription'}}>
            {(() => {
              const regularPlans = subscriptions.filter((p) => p.paymentBackend === 'free' || p.plan_id);
              const contactPlan = subscriptions.find((p) => p.paymentBackend !== 'free' && !p.plan_id);
              const hasMatchingPlan = values.subscriptionId
                && regularPlans.some((p) => p.id === values.subscriptionId);
              const isContactActive = !!(values.subscriptionId && !hasMatchingPlan);

              const renderPlanCard = (plan) => {
                const isActive = plan.paymentBackend === 'free'
                  ? !values.subscriptionId
                  : values.subscriptionId === plan.id;
                const monthLimit = plan.limits?.month;
                const retentionText = formatRetentionDays(plan.retentionDays);

                let planButton = null;
                if (isActive) {
                  planButton = (
                    <ColorButton
                      className={classes.buttonSubscribeNew}
                      color="primary"
                      variant="contained"
                    >
                      <FormattedMessage id="label.active.btn" defaultMessage="Active"/>
                    </ColorButton>
                  );
                } else if (plan.paymentBackend === 'paypal' && plan.plan_id) {
                  planButton = (
                    <Box display="flex" justifyContent="center" p="2">
                      <PayPalButtons
                        className={classes.buttonSubscribe}
                        createSubscription={
                          async (data, actions) => createSubscription(data, actions, plan.plan_id)
                        }
                        onApprove={async () => {
                          await updateCurrentUser(plan.title, monthLimit);
                          setValues({ ...values, subscription: plan.title });
                        }}
                      />
                    </Box>
                  );
                }

                return (
                  <Grid item xs={12} md={3} key={plan.id}>
                    <CardContent style={{height: '100%'}} className={classes.planCards}>
                      <Card style={{height: '100%'}} {...rest}>
                        <CardContent>
                          <Typography align="center" color="textPrimary" gutterBottom variant="h3">
                            {translate(plan.title, getCurrentLanguage(), plan.title)}
                          </Typography>
                          <Typography align="center" color="textPrimary" gutterBottom variant="h1">
                            {formatPrice(plan.price, plan.currency)}
                          </Typography>
                          {monthLimit !== undefined && monthLimit !== null && (
                            <>
                              <Typography align="center" color="textPrimary" variant="body1">
                                {monthLimit.toLocaleString()}
                                {' '}
                                <FormattedMessage id="label.counts" defaultMessage="counts"/>
                              </Typography>
                              <Typography align="center" color="textPrimary" variant="body1">
                                <FormattedMessage id="label.per.month" defaultMessage="per month"/>
                              </Typography>
                            </>
                          )}
                          {retentionText && (
                            <Typography align="center" color="textPrimary" variant="body1">
                              {'Images stored for '}
                              {retentionText}
                            </Typography>
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          {planButton}
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Grid>
                );
              };

              const contactTitle = contactPlan
                ? translate(contactPlan.title, getCurrentLanguage(), contactPlan.title)
                : translate('Enterprise', getCurrentLanguage(), 'Enterprise');
              const contactRetentionText = contactPlan
                ? formatRetentionDays(contactPlan.retentionDays)
                : null;
              const contactMonthLimit = contactPlan?.limits?.month;

              return (
                <>
                  {regularPlans.map(renderPlanCard)}
                  <Grid item xs={12} md={3} key={contactPlan?.id || 'contact'}>
                    <CardContent style={{height: '100%'}} className={classes.planCards}>
                      <Card style={{height: '100%'}} {...rest}>
                        <CardContent>
                          <Typography align="center" color="textPrimary" gutterBottom variant="h3">
                            {contactTitle}
                          </Typography>
                          <Typography align="center" color="textPrimary" gutterBottom variant="h1">
                            <FormattedMessage id="label.contact" defaultMessage="Contact Us"/>
                          </Typography>
                          {contactMonthLimit !== undefined && contactMonthLimit !== null && (
                            <>
                              <Typography align="center" color="textPrimary" variant="body1">
                                {contactMonthLimit.toLocaleString()}
                                {' '}
                                <FormattedMessage id="label.counts" defaultMessage="counts"/>
                              </Typography>
                              <Typography align="center" color="textPrimary" variant="body1">
                                <FormattedMessage id="label.per.month" defaultMessage="per month"/>
                              </Typography>
                            </>
                          )}
                          {contactRetentionText && (
                            <Typography align="center" color="textPrimary" variant="body1">
                              {'Images stored for '}
                              {contactRetentionText}
                            </Typography>
                          )}
                          <Box sx={{ flexGrow: 1 }} />
                          {isContactActive ? (
                            <ColorButton
                              className={classes.buttonSubscribeNew}
                              color="primary"
                              variant="contained"
                              href="/app/contact"
                            >
                              <FormattedMessage id="label.contact.btn" defaultMessage="Contact Us"/>
                            </ColorButton>
                          ) : (
                            <Box display="flex" justifyContent="center" p="2">
                              <Button
                                className={classes.buttonSubscribe}
                                color="primary"
                                variant="contained"
                                href="/app/contact"
                              >
                                <FormattedMessage id="label.contact.btn" defaultMessage="Contact Us"/>
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Grid>
                </>
              );
            })()}
          </PayPalScriptProvider>
        </Grid>
        <Divider />
      </Card>
    </form>
  );
};

ProfileDetails.propTypes = {
  className: PropTypes.string
};

export default connect(getUser, { updateUser })(ProfileDetails);
